import React from "react";
import { MdAdd, MdDeleteOutline } from "react-icons/md";

// Small form pieces shared by the Cancellation Policy tabs.

export const inputCls =
  "border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-gray-400 bg-white";

export const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// Immutable set at a dotted path: setIn(obj, "rules.STANDARD.noShow.enabled", true)
export const setIn = (obj, path, value) => {
  const [head, ...rest] = path.split(".");
  const clone = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  clone[head] = rest.length ? setIn(clone[head], rest.join("."), value) : value;
  return clone;
};

export const Card = ({ title, hint, right, children, muted }) => (
  <section className={`rounded-xl border shadow-sm p-4 ${muted ? "bg-gray-50 border-gray-100" : "bg-white border-gray-100"}`}>
    {(title || right) && (
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          {title && <h3 className="text-sm font-semibold text-gray-900">{title}</h3>}
          {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </section>
);

export const Toggle = ({ checked, onChange, label }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
    <span
      role="switch"
      aria-checked={!!checked}
      tabIndex={0}
      onKeyDown={(e) => (e.key === " " || e.key === "Enter") && (e.preventDefault(), onChange(!checked))}
      onClick={() => onChange(!checked)}
      className={`relative inline-block w-9 h-5 rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-gray-300"}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
    </span>
    {label && <span className="text-sm text-gray-700">{label}</span>}
  </label>
);

// Number box that keeps an empty string while typing; `nullable` lets it be cleared to null.
export const NumberInput = ({ value, onChange, prefix, suffix, width = "w-20", step = 1, nullable, placeholder }) => (
  <span className="inline-flex items-center gap-1">
    {prefix && <span className="text-sm text-gray-500">{prefix}</span>}
    <input
      type="number"
      min={0}
      step={step}
      value={value === null || value === undefined ? "" : value}
      placeholder={placeholder}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "") return onChange(nullable ? null : "");
        onChange(Number(raw));
      }}
      className={`${inputCls} ${width}`}
    />
    {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
  </span>
);

const FEE_TYPES = [
  { value: "FLAT", label: "Flat ₹" },
  { value: "PERCENT", label: "% of booking" },
  { value: "BANDS", label: "By booking value" },
];

const blankFee = (type) => {
  if (type === "PERCENT") return { type, percent: 10, min: 0, max: null };
  if (type === "BANDS") return { type, bands: [{ upTo: 1000, amount: 99 }, { upTo: null, amount: 149 }] };
  return { type: "FLAT", amount: 99 };
};

/** Flat amount, % with min/max, or value bands. */
export const FeeEditor = ({ fee, onChange, disabled }) => {
  const f = fee || blankFee("FLAT");
  return (
    <div className={`flex flex-wrap items-center gap-2 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      <select value={f.type} onChange={(e) => onChange(blankFee(e.target.value))} className={inputCls}>
        {FEE_TYPES.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>
      {f.type === "FLAT" && <NumberInput prefix="₹" value={f.amount} onChange={(v) => onChange({ ...f, amount: v })} />}
      {f.type === "PERCENT" && (
        <>
          <NumberInput value={f.percent} suffix="%" width="w-16" step={0.5} onChange={(v) => onChange({ ...f, percent: v })} />
          <NumberInput prefix="min ₹" value={f.min} onChange={(v) => onChange({ ...f, min: v })} />
          <NumberInput prefix="max ₹" value={f.max} nullable placeholder="none" onChange={(v) => onChange({ ...f, max: v })} />
        </>
      )}
      {f.type === "BANDS" && (
        <div className="w-full space-y-1.5 mt-1">
          {(f.bands || []).map((b, i) => {
            const last = i === f.bands.length - 1;
            const from = i === 0 ? 0 : (f.bands[i - 1].upTo || 0) + 1;
            const update = (patch) => onChange({ ...f, bands: f.bands.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
            return (
              <div key={i} className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span className="w-24">{last && (b.upTo === null || b.upTo === "") ? `${rupees(from)}+` : `${rupees(from)} to`}</span>
                {!(last && (b.upTo === null || b.upTo === "")) && (
                  <NumberInput prefix="₹" value={b.upTo} width="w-24" onChange={(v) => update({ upTo: v })} />
                )}
                <span>→</span>
                <NumberInput prefix="₹" value={b.amount} onChange={(v) => update({ amount: v })} />
                {f.bands.length > 1 && (
                  <button type="button" title="Remove band" onClick={() => onChange({ ...f, bands: f.bands.filter((_, j) => j !== i) })} className="text-gray-400 hover:text-red-500">
                    <MdDeleteOutline />
                  </button>
                )}
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => {
              const bands = [...f.bands];
              const lastBand = bands[bands.length - 1];
              const prevTop = bands.length > 1 ? bands[bands.length - 2].upTo || 0 : 0;
              // Close the open-ended band and add a new open one above it.
              bands[bands.length - 1] = { ...lastBand, upTo: lastBand.upTo ?? prevTop + 2000 };
              bands.push({ upTo: null, amount: lastBand.amount });
              onChange({ ...f, bands });
            }}
            className="text-xs font-semibold text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
          >
            <MdAdd /> Add value band
          </button>
        </div>
      )}
    </div>
  );
};

export const Row = ({ label, hint, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 py-2.5 border-t border-gray-100 first:border-t-0">
    <div className="min-w-[200px]">
      <p className="text-sm text-gray-800">{label}</p>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-3">{children}</div>
  </div>
);
